import { useMemo } from "react";
import { Box, Stack, Typography, Divider } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";

import type { Activity } from "@/data/mock-activities";
import { ACCOUNTS } from "@/data/mock-data";
import { formatUSD } from "@/lib/formatters";

// ---- Props ---------------------------------------------------------------

interface ActivitiesSimpleViewProps {
  /** Filtered + time-sorted by the caller. */
  activities: Activity[];
  /**
   * When true, append the account name under each row's description —
   * necessary in aggregate "All Live" mode so users can tell which
   * account each activity belongs to.
   */
  showAccount?: boolean;
}

// ---- Component -----------------------------------------------------------

/**
 * Robinhood-style activity history, mobile-first. Sections:
 *
 *   1. "Pending" block at the top — any activity with status === "pending".
 *      Dividends with future ex-dates live here.
 *   2. Month groups — "April", "March", … — everything else, newest first.
 *
 * Each row renders:
 *   • Bold description (e.g. "ABBV dividend", "Buy 3 WM")
 *   • Secondary line: date (+ "Pending" pill if applicable) + optional
 *     account + optional "qty shares at $price" for trades
 *   • Signed amount on the right, coloured green for credits / red for debits
 */
export function ActivitiesSimpleView({
  activities,
  showAccount = false
}: ActivitiesSimpleViewProps) {
  const groups = useMemo(() => buildGroups(activities), [activities]);

  if (activities.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No activity matches the current filters.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {groups.map((g) => (
        <Box key={g.key} sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "1.1rem",
              // Matches Robinhood's oversized section header style —
              // "Pending" / "April" sit big and confident, not as a
              // muted overline, so the rhythm of the list feels more
              // magazine than spreadsheet.
              mb: 1
            }}
          >
            {g.title}
          </Typography>
          <Divider sx={{ mb: 0.5 }} />
          {g.items.map((item) => (
            <ActivityRow key={item.id} activity={item} showAccount={showAccount} />
          ))}
        </Box>
      ))}
    </Box>
  );
}

// ---- Row ----------------------------------------------------------------

function ActivityRow({
  activity,
  showAccount
}: {
  activity: Activity;
  showAccount: boolean;
}) {
  const { amount, status } = activity;
  const isPending = status === "pending";
  const positive = amount >= 0;

  // Color assignment:
  //   • credits → success.main (green)
  //   • debits → text.primary (black) — Robinhood doesn't paint
  //     withdrawals/fees red; only dividends + deposits pop green.
  //     Keeping red for the negative cases would overload signal.
  const amountColor = (t: Theme) =>
    positive ? t.palette.success.main : t.palette.text.primary;

  const accountName = showAccount
    ? ACCOUNTS.find((a) => a.id === activity.accountId)?.name
    : undefined;

  const subtitle = buildSubtitle(activity, accountName);

  return (
    <Box
      sx={(t: Theme) => ({
        display: "grid",
        gridTemplateColumns: "1fr auto",
        columnGap: 1.5,
        alignItems: "start",
        py: 1.25,
        borderBottom: `1px solid ${t.palette.divider}`,
        "&:last-of-type": { borderBottom: 0 }
      })}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: "text.primary",
            lineHeight: 1.3,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}
        >
          {activity.description}
        </Typography>
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ mt: 0.25, flexWrap: "wrap" }}
        >
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontSize: 13 }}
          >
            {subtitle}
          </Typography>
          {isPending && <PendingPill />}
        </Stack>
      </Box>
      <Typography
        variant="body1"
        sx={(t) => ({
          fontWeight: 700,
          fontFeatureSettings: '"tnum" 1',
          color: amountColor(t),
          whiteSpace: "nowrap"
        })}
      >
        {formatSignedUSD(amount)}
      </Typography>
    </Box>
  );
}

function PendingPill() {
  return (
    <Box
      component="span"
      sx={(t: Theme) => ({
        display: "inline-flex",
        alignItems: "center",
        px: 0.75,
        py: 0.125,
        borderRadius: 1,
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1.4,
        bgcolor: alpha(t.palette.warning.main, 0.16),
        color: t.palette.warning.main,
        letterSpacing: 0.3,
        textTransform: "uppercase"
      })}
    >
      Pending
    </Box>
  );
}

// ---- Helpers -------------------------------------------------------------

interface Group {
  /** Stable React key — `pending` or `YYYY-MM`. Sergey 2026-05-01:
   *  was previously using `title` as key, which collides when the
   *  feed spans multiple years (e.g. May 2025 + May 2026 both keyed
   *  "May"). React reused the DOM of the first match across renders,
   *  so when a filter narrowed the items, the previous render's
   *  child rows leaked through. Year-month key fixes the
   *  reconciliation. */
  key: string;
  title: string;
  items: Activity[];
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

/**
 * Split activities into "Pending" + month groups. Within each group
 * the caller's sort order is preserved (we rely on upstream newest-
 * first sorting so rows descend chronologically within a month).
 */
function buildGroups(activities: Activity[]): Group[] {
  const pending: Activity[] = [];
  const byMonth = new Map<string, Activity[]>();

  for (const a of activities) {
    if (a.status === "pending") {
      pending.push(a);
      continue;
    }
    const d = new Date(a.timestamp);
    // Key on year + month so Jan 2025 and Jan 2026 don't collide when
    // the window grows. Label still uses just the month name since
    // rolling 12-month history is rarely ambiguous for demo data.
    const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth()).padStart(2, "0")}`;
    const bucket = byMonth.get(key) ?? [];
    bucket.push(a);
    byMonth.set(key, bucket);
  }

  const groups: Group[] = [];
  if (pending.length > 0) {
    groups.push({ key: "pending", title: "Pending", items: pending });
  }
  // Sort month keys descending so newer months appear first.
  const sortedKeys = Array.from(byMonth.keys()).sort((a, b) =>
    a < b ? 1 : -1
  );
  // Detect whether the data spans more than one calendar year. When
  // it does, append the year to the display title so the user can
  // tell "April 2025" from "April 2026" at a glance. Single-year
  // history keeps the original bare month name.
  const years = new Set(sortedKeys.map((k) => k.split("-")[0]));
  const multiYear = years.size > 1;
  for (const k of sortedKeys) {
    const [yearStr, monthStr] = k.split("-");
    const monthIdx = Number(monthStr);
    const baseTitle = MONTH_NAMES[monthIdx] ?? k;
    const title = multiYear ? `${baseTitle} ${yearStr}` : baseTitle;
    groups.push({ key: k, title, items: byMonth.get(k) ?? [] });
  }
  return groups;
}

/**
 * Build the subtitle line for an activity row. Shape depends on type:
 *   • Trade: "Apr 17 · 3 shares at $223.40" (optionally · Alpaca Live)
 *   • Dividend (settled): "Apr 17"
 *   • Dividend (pending): "May 15" (the "Pending" pill sits alongside)
 *   • Fee: "Apr 17"
 *   • Transfer: "Apr 14"
 *   • Interest: "Mar 17"
 *
 * Account name is appended with " · " separator when showAccount is on.
 */
function buildSubtitle(a: Activity, accountName?: string): string {
  const date = new Date(a.timestamp);
  const dateLabel = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric"
  });

  const parts: string[] = [dateLabel];

  if (a.type === "trade" && a.qty !== null && a.qty !== undefined && a.price) {
    const qtyLabel = a.qty % 1 === 0 ? a.qty.toString() : a.qty.toFixed(6);
    parts.push(`${qtyLabel} shares at ${formatUSD(a.price)}`);
  }

  if (accountName) parts.push(accountName);
  return parts.join(" · ");
}

/**
 * Signed USD formatter. Robinhood uses "+$0.19" for credits and
 * "-$250.00" for debits — `formatUSD` already emits the "-" prefix but
 * strips positive signs, so we add the "+" manually.
 */
function formatSignedUSD(n: number): string {
  if (n > 0) return `+${formatUSD(n)}`;
  return formatUSD(n);
}
